import { useMutation } from "@tanstack/react-query"
import { Form, Input, Button, message, Space, Row, Col } from "antd"
import { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  AuthActions,
  AuthContext,
  AuthForms,
} from "../../contexts/auth/AuthContext"
import { LOGIN, REGISTER } from "./mutations"

const SignInForm = () => {
  const { t } = useTranslation()
  const [isRegistering, setIsRegistering] = useState(false)
  const { authState, authDispatch } = useContext(AuthContext)
  const {
    mutate: login,
    isLoading: loading,
    error,
    data,
  } = useMutation(LOGIN, {
    onSuccess: (data) => {
      if (data?.accessToken) {
        authDispatch({
          type: AuthActions.SIGN_IN,
          payload: {
            user: data?.user,
            accessToken: data?.accessToken,
            refreshToken: data?.refreshToken,
          },
        })
        message.success(t("welcomeBack"))
      }
    },
    onError: (error) => {
      if (!data?.accessToken) {
        message.error(t("invalidCredentials"))
        return
      }
    },
  })
  const {
    mutate: register,
    isLoading: rLoading,
    error: rError,
  } = useMutation(REGISTER, {
    onSuccess: (data) => {
      if (data) {
        message.success(t("registered"))
        setIsRegistering(false)
      } else if (!data) {
        message.error(t("error"))
      }
    },
    onError: (error) => {
      message.error(t("error"))
    },
  })

  async function onFinish(values: any) {
    if (!values) return

    if (isRegistering) {
      try {
        await register({
          lastname: values.lastname,
          name: values.name,
          middlename: values.middlename,
          address: values.address,
          phone: values.phone,
          email: values.email,
          password: values.password,
          confirmPassword: values.confirmPassword,
        })
      } catch (error: any) {
        message.error(error.toString())
      }
      return
    }

    if (authState.currentForm === AuthForms.FORGOT_PASSWORD) {
      message.success(t("newPasswordSend"))
      authDispatch({ type: AuthActions.TOGGLE_FORM })
      return
    }

    if (!isRegistering) {
      try {
        await login({
          email: values.email,
          password: values.password,
        })
      } catch (err: any) {
        message.error(err.toString())
      }
    }
  }

  return (
    <Form
      name="sign-in-form"
      className="login-form"
      initialValues={{ remember: true }}
      layout="vertical"
      style={{ width: "100%" }}
      onFinish={onFinish}
    >
      {!isRegistering ? (
        <>
          <Form.Item
            name="email"
            label={t("email")}
            rules={[{ required: true, message: t("emailPlaceholder") }]}
          >
            <Input
              size="large"
              autoComplete="email"
              type="email"
              placeholder={t("emailPlaceholder")}
            />
          </Form.Item>

          {authState.currentForm !== AuthForms.FORGOT_PASSWORD && (
            <Form.Item
              name="password"
              label={t("password")}
              rules={[{ required: true, message: t("passwordPlaceholder") }]}
            >
              <Input.Password
                size="large"
                autoComplete="currentPassword"
                type="password"
                placeholder={t("passwordPlaceholder")}
              />
            </Form.Item>
          )}
        </>
      ) : (
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="name"
              label={t("name")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="text"
                placeholder={t("name")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="middlename"
              label={t("middlename")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="text"
                placeholder={t("middlename")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="lastname"
              label={t("lastname")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="text"
                placeholder={t("lastname")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label={t("email")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="email"
                addonAfter="@"
                placeholder={t("email")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="address"
              label={t("address")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="address"
                placeholder={t("address")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="phone"
              label={t("phone")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="text"
                placeholder={t("phone")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item name="password" label={t("password")}>
              <Input.Password
                style={{ width: "100%" }}
                size="large"
                placeholder={t("password")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="confirmPassword" label={t("confirmPassword")}>
              <Input.Password
                style={{ width: "100%" }}
                size="large"
                placeholder={t("confirmPassword")}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Space
        style={{
          justifyContent: "space-between",
          width: "100%",
          marginBottom: 24,
        }}
      >
        <Button
          style={{ paddingLeft: 0 }}
          type="link"
          onClick={() => authDispatch({ type: AuthActions.TOGGLE_FORM })}
          className="login-form-forgot"
        >
          {authState.currentForm === AuthForms.FORGOT_PASSWORD
            ? t("rememberedPassword")
            : t("forgotPassword")}
        </Button>
        <Button
          style={{ paddingLeft: 0 }}
          type="link"
          onClick={() => setIsRegistering(!isRegistering)}
          className="login-form-forgot"
        >
          {isRegistering ? t("signIn") : t("register")}
        </Button>
      </Space>

      <Button
        block
        type="primary"
        htmlType="submit"
        loading={loading || rLoading}
        danger={!!error || !!rError}
        className="login-form-button"
      >
        {authState.currentForm === AuthForms.FORGOT_PASSWORD
          ? t("send")
          : !isRegistering
          ? t("signIn")
          : t("register")}
      </Button>
    </Form>
  )
}

export default SignInForm
